import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DragIndicator,
  Delete,
  Add,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FeaturedProduct {
  id: string;
  category_code: number;
  product_id: string;
  display_order: number;
  tag: string | null;
  is_active: boolean;
  product_name: string;
  product_slug: string;
  price: number;
  original_price: number | null;
  brand_name: string | null;
}

interface Category {
  code: number;
  name: string;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  originalPrice?: number | null;
  discount?: number | null;
  brand: string | null;
  imageUrl?: string | null;
}

interface SortableItemProps {
  product: FeaturedProduct;
  onToggleActive: (id: string, currentActive: boolean) => void;
  onDelete: (id: string) => void;
}

function SortableItem({ product, onToggleActive, onDelete }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 모바일용 액션 버튼
  const actionButtons = (
    <Box sx={{
      display: 'flex',
      gap: { xs: 0.5, sm: 1 },
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: { xs: 'flex-end', sm: 'flex-start' },
    }}>
      <Chip
        label={product.is_active ? '활성' : '비활성'}
        color={product.is_active ? 'success' : 'default'}
        size="small"
        onClick={() => onToggleActive(product.id, product.is_active)}
        sx={{ cursor: 'pointer' }}
      />
      <IconButton edge="end" onClick={() => onDelete(product.id)} size="small" color="error">
        <Delete />
      </IconButton>
    </Box>
  );

  if (isMobile) {
    // 모바일: 컴팩트한 카드형 배치
    return (
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          bgcolor: 'background.paper',
          mb: 1.5,
          p: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Drag Handle & Order */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              pt: 0.5
            }}
          >
            <IconButton
              {...attributes}
              {...listeners}
              size="small"
              sx={{
                cursor: 'move',
                p: 0.5,
                bgcolor: 'grey.50',
                '&:active': { cursor: 'grabbing' }
              }}
            >
              <DragIndicator fontSize="small" color="action" />
            </IconButton>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                boxShadow: 1,
              }}
            >
              {product.display_order}
            </Box>
          </Box>

          {/* Product Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5, fontSize: '0.95rem' }}>
                {product.product_name}
              </Typography>
              <IconButton
                onClick={() => onDelete(product.id)}
                size="small"
                sx={{
                  mt: -1,
                  mr: -1,
                  color: 'text.disabled',
                  '&:hover': { color: 'error.main' }
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ bgcolor: 'grey.100', px: 0.8, py: 0.3, borderRadius: 1 }}>
                {product.brand_name || 'No Brand'}
              </Typography>
              {product.tag && (
                <Chip
                  label={product.tag}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                />
              )}
            </Box>

            <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ fontSize: '1rem' }}>
                {product.price.toLocaleString()}원
              </Typography>
              <Chip
                label={product.is_active ? '노출중' : '숨김'}
                color={product.is_active ? 'success' : 'default'}
                size="small"
                variant={product.is_active ? 'filled' : 'outlined'}
                onClick={() => onToggleActive(product.id, product.is_active)}
                sx={{ height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // 데스크톱: 가로 배치
  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        mb: 1,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        pr: 1,
      }}
      secondaryAction={actionButtons}
    >
      <IconButton {...attributes} {...listeners} sx={{ cursor: 'move', mr: 1 }}>
        <DragIndicator />
      </IconButton>
      <Box
        sx={{
          width: 48,
          height: 48,
          bgcolor: 'grey.200',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.secondary">
          {product.display_order}
        </Typography>
      </Box>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1">{product.product_name}</Typography>
            {product.tag && (
              <Chip label={product.tag} size="small" color="primary" />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {product.brand_name || '브랜드 없음'}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="text.primary">
              {product.price.toLocaleString()}원
            </Typography>
          </Box>
        }
        sx={{ pr: 2 }}
      />
    </ListItem>
  );
}

export default function FeaturedProducts() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedCategory, setSelectedCategory] = useState<number>(10000);
  const [featuredProducts, setFeaturedProducts] = useState<FeaturedProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add product dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch all categories on mount
  useEffect(() => {
    async function fetchCategories() {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/v1/categories?simple=true`);
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          // level=0인 대분류만 필터링
          const categoryList = result.data
            .filter((cat: any) => cat.level === 0)
            .map((cat: any) => ({
              code: cat.code,
              name: cat.name,
            }))
            .sort((a: any, b: any) => a.code - b.code);

          setCategories(categoryList);

          if (categoryList.length > 0) {
            setSelectedCategory(categoryList[0].code);
          }
        }
      } catch (err) {
        console.error('카테고리 로딩 실패:', err);
      } finally {
        setCategoriesLoading(false);
      }
    }

    fetchCategories();
  }, []);

  // Fetch featured products for selected category
  useEffect(() => {
    fetchFeaturedProducts();
  }, [selectedCategory]);

  const fetchFeaturedProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/v1/admin/category-featured/${selectedCategory}`
      );
      const result = await response.json();

      if (result.success) {
        setFeaturedProducts(result.data);
      } else {
        throw new Error(result.message || '데이터를 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Error fetching featured products:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = featuredProducts.findIndex((p) => p.id === active.id);
    const newIndex = featuredProducts.findIndex((p) => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newProducts = arrayMove(featuredProducts, oldIndex, newIndex);
    const updatedProducts = newProducts.map((p, index) => ({
      ...p,
      display_order: index + 1,
    }));

    setFeaturedProducts(updatedProducts);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/v1/admin/category-featured/reorder/${selectedCategory}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: updatedProducts.map((p) => ({
              id: p.id,
              displayOrder: p.display_order,
            })),
          }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || '순서 변경 실패');
      }

      await fetchFeaturedProducts();
    } catch (err) {
      console.error('Error reordering products:', err);
      alert(
        '순서 변경 중 오류가 발생했습니다: ' +
        (err instanceof Error ? err.message : String(err))
      );
      await fetchFeaturedProducts();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/v1/admin/category-featured/item/${id}`,
        {
          method: 'DELETE',
        }
      );

      const result = await response.json();

      if (result.success) {
        alert('삭제되었습니다.');
        fetchFeaturedProducts();
      } else {
        throw new Error(result.message || '삭제 실패');
      }
    } catch (err) {
      alert(
        '삭제 중 오류가 발생했습니다: ' +
        (err instanceof Error ? err.message : String(err))
      );
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/v1/admin/category-featured/item/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isActive: !currentActive,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        fetchFeaturedProducts();
      } else {
        throw new Error(result.message || '수정 실패');
      }
    } catch (err) {
      alert(
        '수정 중 오류가 발생했습니다: ' +
        (err instanceof Error ? err.message : String(err))
      );
    }
  };

  const handleSearchProducts = async () => {
    if (!searchKeyword.trim()) {
      alert('검색어를 입력해주세요');
      return;
    }

    setSearchLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/v1/products?search=${encodeURIComponent(searchKeyword)}&limit=20`
      );
      const result = await response.json();

      if (result.success) {
        // API 응답 형식: {success, data: {products: [], pagination: {}}}
        const products = result.data.products || result.data || [];
        setSearchResults(products);
      } else {
        throw new Error(result.message || '상품 검색 실패');
      }
    } catch (err) {
      alert(
        '상품 검색 중 오류가 발생했습니다: ' +
        (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!selectedProductId) {
      alert('상품을 선택해주세요');
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/v1/admin/category-featured`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            categoryCode: selectedCategory,
            productId: selectedProductId,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert('추천 상품이 추가되었습니다');
        setOpenAddDialog(false);
        setSearchKeyword('');
        setSearchResults([]);
        setSelectedProductId(null);
        fetchFeaturedProducts();
      } else {
        throw new Error(result.message || '추가 실패');
      }
    } catch (err) {
      alert(
        '추가 중 오류가 발생했습니다: ' +
        (err instanceof Error ? err.message : String(err))
      );
    }
  };

  const handleOpenAddDialog = () => {
    if (featuredProducts.length >= 10) {
      alert('카테고리별 최대 10개까지만 등록 가능합니다');
      return;
    }
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSearchKeyword('');
    setSearchResults([]);
    setSelectedProductId(null);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        mb: { xs: 2, sm: 3 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
      }}>
        <IconButton
          onClick={() => navigate('/')}
          size={isMobile ? 'small' : 'medium'}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          fontWeight="bold"
          sx={{ flex: 1, minWidth: 0 }}
        >
          MD'S PICK 관리
        </Typography>
      </Box>

      {/* Category Selector */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
          <FormControl fullWidth>
            <InputLabel id="category-select-label">카테고리 선택</InputLabel>
            {categoriesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  카테고리 로딩 중...
                </Typography>
              </Box>
            ) : categories.length === 0 ? (
              <Alert severity="error">카테고리를 불러올 수 없습니다.</Alert>
            ) : (
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                label="카테고리 선택"
                onChange={(e) => setSelectedCategory(Number(e.target.value))}
                size={isMobile ? 'small' : 'medium'}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.code} value={cat.code}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        </CardContent>
      </Card>

      {/* Featured Products List */}
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
              추천 상품 목록 ({featuredProducts.length}/10)
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <Add />}
              onClick={handleOpenAddDialog}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? '+' : '추가'}
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                로딩 중...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : featuredProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                등록된 추천 상품이 없습니다.
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={featuredProducts.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <List sx={{ p: 0 }}>
                  {featuredProducts.map((product) => (
                    <SortableItem
                      key={product.id}
                      product={product}
                      onToggleActive={handleToggleActive}
                      onDelete={handleDelete}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Paper sx={{
        mt: { xs: 2, sm: 3 },
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'info.lighter'
      }}>
        <Typography
          variant={isMobile ? 'body2' : 'subtitle2'}
          fontWeight="bold"
          gutterBottom
        >
          안내사항
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: { xs: 1.5, sm: 2 } }}>
          <li>
            <Typography variant={isMobile ? 'caption' : 'body2'}>
              카테고리별 최대 10개의 추천 상품을 등록할 수 있습니다.
            </Typography>
          </li>
          <li>
            <Typography variant={isMobile ? 'caption' : 'body2'}>
              순서는 1-10 사이의 숫자로 표시됩니다.
            </Typography>
          </li>
          <li>
            <Typography variant={isMobile ? 'caption' : 'body2'}>
              왼쪽의 드래그 핸들을 클릭하여 순서를 변경할 수 있습니다.
            </Typography>
          </li>
          <li>
            <Typography variant={isMobile ? 'caption' : 'body2'}>
              카테고리 페이지에서는 1행에 5개씩 보이며, 총 10개가 좌우로 슬라이드됩니다.
            </Typography>
          </li>
        </Box>
      </Paper>

      {/* Add Product Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant={isMobile ? 'h6' : 'h5'}>추천 상품 추가</Typography>
            {isMobile && (
              <IconButton onClick={handleCloseAddDialog} edge="end">
                <ArrowBack />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            mb: { xs: 2, sm: 3 },
            mt: 1,
            flexDirection: { xs: 'column', sm: 'row' },
          }}>
            <TextField
              fullWidth
              label="상품 검색"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchProducts();
                }
              }}
              placeholder="상품명으로 검색"
              size={isMobile ? 'small' : 'medium'}
            />
            <Button
              variant="contained"
              onClick={handleSearchProducts}
              disabled={searchLoading}
              fullWidth={isMobile}
              sx={{ minWidth: { xs: 'auto', sm: '100px' } }}
            >
              {searchLoading ? '검색 중...' : '검색'}
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <>
              <Typography
                variant={isMobile ? 'body2' : 'subtitle1'}
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                검색 결과 ({searchResults.length}개)
              </Typography>
              {isMobile ? (
                <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 0 }}>
                  {searchResults.map((product) => (
                    <Box
                      key={product.id}
                      onClick={() => setSelectedProductId(product.id)}
                      sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        bgcolor: selectedProductId === product.id ? 'primary.lighter' : 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s',
                        '&:last-child': { borderBottom: 0 }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.brand || 'No Brand'}
                          </Typography>
                        </Box>
                        {product.discount && (
                          <Chip label={`${product.discount}%`} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="radio"
                            checked={selectedProductId === product.id}
                            onChange={() => setSelectedProductId(product.id)}
                            style={{ margin: 0, marginRight: 8 }}
                          />
                          <Typography variant="caption" color={selectedProductId === product.id ? 'primary' : 'text.secondary'}>
                            {selectedProductId === product.id ? '선택됨' : '선택하기'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          {product.originalPrice && product.originalPrice > product.price && (
                            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', mr: 1 }}>
                              {product.originalPrice.toLocaleString()}
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {product.price.toLocaleString()}원
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </List>
              ) : (
                <TableContainer sx={{
                  maxHeight: { xs: 300, sm: 400 },
                  mb: { xs: 2, sm: 3 },
                  overflowX: 'auto',
                }}>
                  <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">선택</TableCell>
                        <TableCell>상품명</TableCell>
                        {!isMobile && <TableCell>브랜드</TableCell>}
                        <TableCell align="right">가격</TableCell>
                        {!isMobile && <TableCell align="right">할인율</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((product) => (
                        <TableRow
                          key={product.id}
                          selected={selectedProductId === product.id}
                          hover
                          onClick={() => setSelectedProductId(product.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <input
                              type="radio"
                              checked={selectedProductId === product.id}
                              onChange={() => setSelectedProductId(product.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ wordBreak: 'break-word' }}>
                              {product.name}
                            </Typography>
                            {isMobile && product.brand && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {product.brand}
                              </Typography>
                            )}
                          </TableCell>
                          {!isMobile && <TableCell>{product.brand || '-'}</TableCell>}
                          <TableCell align="right">
                            <Typography variant={isMobile ? 'caption' : 'body2'}>
                              {product.price.toLocaleString()}원
                            </Typography>
                            {product.originalPrice && product.originalPrice > product.price && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {product.originalPrice.toLocaleString()}원
                              </Typography>
                            )}
                            {isMobile && product.discount && (
                              <Typography variant="caption" color="error" display="block">
                                {product.discount}% 할인
                              </Typography>
                            )}
                          </TableCell>
                          {!isMobile && (
                            <TableCell align="right">
                              {product.discount ? `${product.discount}%` : '-'}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button onClick={handleCloseAddDialog} size={isMobile ? 'small' : 'medium'}>
            취소
          </Button>
          <Button
            onClick={handleAddProduct}
            variant="contained"
            disabled={!selectedProductId}
            size={isMobile ? 'small' : 'medium'}
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
